import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import DynamicTable from "../../unicomponents/Tables/DynamicTable";
import UpdateStuInfoModal from "./components/UpdateStuInfoModal";
import LoadingModal from "../../unicomponents/LoadingModal";
import useStudentInfo from "../../hooks/useStudentInfo";
import HelpHyperLink from "../../unicomponents/HelpHyperLink";
import useOrganizationSetting from "../../hooks/useOrganizationSetting";
import {
  createFilteredAndSortedOptions,
  uniqueDataOptionsKeys,
} from "../../util/dealingArrObj";
import useRegistrationInfo from "../../hooks/useRegistrationInfo";
import { useAuthContext } from "../../context/AuthContext";
import { useUIContext } from "../../context/UIContext";
import LoadingInside from "../../unicomponents/LoadingInside";
import SimpleAlertMessageBlock from "../../unicomponents/SimpleAlertMessageBlock";
import { checkHrteachers } from "../../util/checkPermissions";
import { useTranslation } from "react-i18next";
import useFlexibleData from "../../hooks/useFlexibleData";
import {
  STUDENT_INFO_DIRECT_FIELD_KEYS,
  STUDENT_INFO_JSON_BAG_KEY,
  getActiveCustomFields,
  normalizeSiblingsArray,
  normalizeStudentInfoCustomSettings,
  parseJsonBag,
} from "./studentInfoCustomFields";

export default function StudentInfo() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  // 배열의 두 번째 요소를 schoolId로 사용합니다.
  const schoolId = paths[1];
  //아래 조건으로 데이터를 불러오도록 합니다.
  const conditions = { schoolid: schoolId, role: "student" };
  const [rows, setRows] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [filteredSetting, setFilteredSetting] = useState({});
  const [filterOrgan, setFilterOrgan] = useState({});
  const [idsForKey, setIdsForKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isUpLoading, setIsUpLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState(false);
  const [organPathForSort, setOrganPathForSort] = useState("/");

  const {
    studentInfoQuery: { isLoading, data: studentData },
  } = useStudentInfo(conditions);

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);

  const {
    flexibleDataQuery: { data: customFieldData },
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "studentInfo",
    sub_category: "customFields",
  });

  const {
    hrTeachersQuery: { data: hrTeachersData },
  } = useRegistrationInfo(
    { schoolid: schoolId },
    { registrationInfo: false, hrTeachers: true },
  );

  const { handleToastCenterTop } = useUIContext();

  const { hasPermission } = useAuthContext();

  const customFieldSettings = useMemo(
    () =>
      normalizeStudentInfoCustomSettings(
        customFieldData?.[0]?.data_value || {},
      ),
    [customFieldData],
  );

  const tableCustomFields = useMemo(
    () => getActiveCustomFields(customFieldSettings, "table"),
    [customFieldSettings],
  );

  // useEffect(() => {
  //   console.log("studentData", studentData);
  //   setRows(studentData || []);
  //   // console.log("studentData", studentData);
  // }, [studentData]);

  useEffect(() => {
    if (schoolYear === null) {
      //초기 데이터를 학년도로 필터해서 제공해야 하기 때문에 오름차순 제일 마지막 걸로
      let initialSchoolYear = null;
      let initialSchoolYearId = null;
      if (settingData && settingData.length > 0) {
        // console.log("settingData", settingData);
        const initialData = settingData.sort(
          (a, b) => b.school_year - a.school_year,
        )[0];
        initialSchoolYear = initialData.school_year;
        initialSchoolYearId = initialData.id;
      }
      setSchoolYear(initialSchoolYear);

      //드랍다운용이다.
      const filtered = settingData.filter(
        (r) => r.school_year === initialSchoolYear,
      );
      setFilteredSetting(filtered);

      setFilterOrgan((prevFilter) => ({
        ...prevFilter,
        0: initialSchoolYearId,
      }));

      const forIdsForKey = uniqueDataOptionsKeys(
        filtered,
        "hierarchy_level",
        "level_name_new",
      );
      setIdsForKey(forIdsForKey);

      // console.log("initialSchoolYear", initialSchoolYear);
      // console.log("organPathForSort", organPathForSort);

      //rows 데이터처리
      if (studentData && settingData && initialSchoolYear) {
        const sortedRows = generateRows(
          studentData,
          initialSchoolYear,
          organPathForSort,
        );
        setRows(sortedRows);
        // console.log("sortedRows1", sortedRows);
      }
    } else {
      //rows 데이터처리
      if (studentData && settingData) {
        // console.log("settingData", settingData);
        // console.log("studentData", studentData);

        const sortedRows = generateRows(
          studentData,
          schoolYear,
          organPathForSort,
        );
        setRows(sortedRows);
        // console.log("sortedRows2", sortedRows);
      }
    }
  }, [settingData, schoolYear, studentData, organPathForSort]);

  function generateRows(studentData, schoolYear, organPathForSort = "/") {
    if (schoolYear === "전체") {
      return studentData;
    }
    let toBeRows = studentData
      .map((user) => ({
        ...user,
        registrationinfos: user.registrationinfos.filter((info) => {
          return info.school_year === schoolYear;
        }),
      }))
      .filter((user) => user.registrationinfos.length > 0)
      .sort((a, b) => {
        if (a.fullname < b.fullname) return -1;
        if (a.fullname > b.fullname) return 1;
        return 0;
      })
      .sort((a, b) => {
        if (
          a.registrationinfos[0].organization_path <
          b.registrationinfos[0].organization_path
        )
          return -1;
        if (
          a.registrationinfos[0].organization_path >
          b.registrationinfos[0].organization_path
        )
          return 1;
        return 0;
      });

    // console.log("organPathForSort", organPathForSort);

    if (organPathForSort !== "/" && !organPathForSort.startsWith("nu")) {
      toBeRows = toBeRows
        .map((user) => ({
          ...user,
          registrationinfos: user.registrationinfos.filter((info) =>
            info.organization_path.includes(organPathForSort),
          ),
        }))
        .filter((user) => user.registrationinfos.length > 0);
    }

    return toBeRows;
  }

  const { arrayData, displayKey, valueKey, filters, idKey, sortKey, isDesc } = {
    arrayData: settingData,
    displayKey: "school_year",
    valueKey: "id",
    filters: { hierarchy_level: 0, parent_id: 0 },
    idKey: "id",
    sortKey: "school_year",
    isDesc: true,
  };

  const options = useMemo(() => {
    return createFilteredAndSortedOptions({
      arrayData,
      displayKey,
      valueKey,
      filters,
      idKey,
      sortKey,
      isDesc,
    });
  }, [arrayData, displayKey, valueKey, filters, idKey, sortKey, isDesc]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;

    const organPath =
      e.target.selectedOptions[0].getAttribute("data-name") + "/" + value;

    setOrganPathForSort(organPath);
    // setRows(
    //   generateRows(studentData, schoolYear, organPath)
    // );

    if (name === "0") {
      const filtered = settingData.filter(
        (r) => r.school_year === selectedOptionText,
      );
      setFilteredSetting(filtered);
      const uniqueOptions = uniqueDataOptionsKeys(
        filtered,
        "hierarchy_level",
        "level_name_new",
      );
      setIdsForKey(uniqueOptions);
      setSchoolYear(selectedOptionText);
    }
    // console.log("addRegi", addRegi);
    setFilterOrgan((prevFilter) => {
      const newFilter = { ...prevFilter, [name]: value };
      // console.log("newFilter", newFilter);
      Object.keys(newFilter).forEach((key) => {
        if (Number(key) > Number(name)) {
          delete newFilter[key];
        }
      });
      return newFilter;
    });

    // console.log("filterOrgan2", filterOrgan);
  };

  const handleUpdateClick = useCallback((row) => {
    const studentInfoRow = row.studentinfos?.[0] || {};
    const rowInfo = {
      id: studentInfoRow.id || null,
      user_id: row.id || null,
      schoolid: row.schoolid || null,
      fullname: row.fullname || null,
      username: row.username || null,
      korean_id_no: studentInfoRow.korean_id_no || null,
      memo: studentInfoRow.memo || null,
      address: studentInfoRow.address || null,
      address_detail: studentInfoRow.address_detail || null,
      entrance_date: studentInfoRow.entrance_date || null,
      phone_number: studentInfoRow.phone_number || null,
      father_name: studentInfoRow.father_name || null,
      father_phone: studentInfoRow.father_phone || null,
      mother_name: studentInfoRow.mother_name || null,
      mother_phone: studentInfoRow.mother_phone || null,
      siblings: Array.isArray(studentInfoRow.siblings)
        ? studentInfoRow.siblings
        : null,
      zipcode: studentInfoRow.zipcode || null,
      studentPhoto_link: studentInfoRow.studentPhoto_link || null,
      graduation_no: studentInfoRow.graduation_no || null,
      academic_record: studentInfoRow.academic_record || null,
      special_record: studentInfoRow.special_record || null,
      enrollment_status: studentInfoRow.enrollment_status || null,
      termination_date: studentInfoRow.termination_date || null,
      [STUDENT_INFO_JSON_BAG_KEY]:
        studentInfoRow[STUDENT_INFO_JSON_BAG_KEY] || "",
    };

    STUDENT_INFO_DIRECT_FIELD_KEYS.forEach((key) => {
      rowInfo[key] = studentInfoRow[key] || null;
    });

    setStudentInfo(rowInfo);
    setModalOpen(true);
  }, []);

  const tableRows = useMemo(
    () =>
      rows.map((row) => {
        const studentInfoRow = row.studentinfos?.[0] || {};
        const jsonBag = parseJsonBag(studentInfoRow[STUDENT_INFO_JSON_BAG_KEY]);
        const customValues = {};

        tableCustomFields.forEach((field) => {
          customValues[field.storage === "json" ? field.id : field.key] =
            field.storage === "json"
              ? jsonBag[field.id] || ""
              : studentInfoRow[field.key] || "";
        });

        return {
          ...row,
          other_name: row.other_name || "",
          korean_id_no: studentInfoRow.korean_id_no || "",
          entrance_date: studentInfoRow.entrance_date || "",
          home_address: `${studentInfoRow.address || ""} ${
            studentInfoRow.address_detail || ""
          }`.trim(),
          phone_number: studentInfoRow.phone_number || "",
          siblings: normalizeSiblingsArray(studentInfoRow.siblings).map(
            (sibling) => sibling.sname,
          ),
          father_name: studentInfoRow.father_name || "",
          father_phone: studentInfoRow.father_phone || "",
          mother_name: studentInfoRow.mother_name || "",
          mother_phone: studentInfoRow.mother_phone || "",
          enrollment_status: studentInfoRow.enrollment_status || "",
          memo: studentInfoRow.memo || "",
          ...customValues,
        };
      }),
    [rows, tableCustomFields],
  );

  const hasOtherName = useMemo(
    () => rows.some((r) => r.other_name),
    [rows],
  );

  const searchFields = useMemo(() => {
    const fields = ["fullname", "username"];
    if (hasOtherName) fields.push("other_name");
    return fields;
  }, [hasOtherName]);

  const tableConfig = useMemo(() => {
    const defaultHeaders = [
      {
        headerKey: "fullname",
        label: t("common.user.fullname") || "이름",
        style: { minWidth: "90px", textAlign: "center" },
        sortField: "fullname",
      },
      {
        headerKey: "username",
        label: t("common.user.username") || "사용자계정",
        style: { minWidth: "110px" },
        sortField: "username",
      },
      ...(hasOtherName
        ? [
            {
              headerKey: "other_name",
              label: t("common.user.otherName") || "다른 이름",
              style: { minWidth: "90px" },
              sortField: "other_name",
            },
          ]
        : []),
      {
        headerKey: "gender",
        label: t("common.user.gender") || "성별",
        style: { width: "40px" },
        sortField: "gender",
      },
      {
        headerKey: "birthday",
        label: t("common.user.birthday") || "생년월일",
        style: { width: "85px" },
        sortField: "birthday",
      },
      {
        headerKey: "idatschool",
        label: t("common.user.idatschool") || "학번",
        style: { width: "70px" },
        sortField: "idatschool",
      },
      {
        headerKey: "korean_id_no",
        label: t("teacher.studentInfo.registerationNo"),
        style: { width: "80px" },
        sortField: "korean_id_no",
      },
      {
        headerKey: "entrance_date",
        label: t("teacher.studentInfo.entranceDate"),
        style: { width: "100px" },
        sortField: "entrance_date",
      },
      {
        headerKey: "home_address",
        label: t("teacher.studentInfo.address"),
        style: { minWidth: "170px" },
        sortField: "home_address",
      },
      {
        headerKey: "phone_number",
        label: t("teacher.studentInfo.studentPhone"),
        style: { width: "72px" },
        sortField: "phone_number",
      },
      {
        headerKey: "siblings",
        label: t("teacher.studentInfo.siblings"),
        style: { width: "80px" },
        sortField: "siblings",
      },
      {
        headerKey: "father_name",
        label: t("teacher.studentInfo.fatherName"),
        style: { width: "80px" },
        sortField: "father_name",
      },
      {
        headerKey: "father_phone",
        label: t("teacher.studentInfo.fatherPhone"),
        style: { width: "90px" },
        sortField: "father_phone",
      },
      {
        headerKey: "mother_name",
        label: t("teacher.studentInfo.motherName"),
        style: { width: "80px" },
        sortField: "mother_name",
      },
      {
        headerKey: "mother_phone",
        label: t("teacher.studentInfo.motherPhone"),
        style: { width: "90px" },
        sortField: "mother_phone",
      },
      {
        headerKey: "enrollment_status",
        label: t("common.user.status") || "상태",
        style: { width: "70px" },
        sortField: "enrollment_status",
      },
      {
        headerKey: "memo",
        label: t("teacher.studentInfo.memo"),
        style: { width: "110px" },
        sortField: "memo",
      },
    ];

    const customHeaders = tableCustomFields.map((field) => ({
      headerKey: field.storage === "json" ? field.id : field.key,
      label: field.label,
      style: { minWidth: field.type === "textarea" ? "160px" : "110px" },
      sortField: field.storage === "json" ? field.id : field.key,
    }));

    const headers = [...defaultHeaders, ...customHeaders];
    const ellipsisCellStyle = { maxWidth: "120px" };
    const ellipsisTextStyle = {
      display: "block",
      maxWidth: "110px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };
    const wrapCellStyle = {
      maxWidth: "170px",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    };

    return {
      style: { fontSize: "13px", tableLayout: "auto" },
      headers,
      columns: headers.map((header) => {
        if (header.headerKey === "fullname") {
          return {
            valueKey: "fullname",
            valueFormatter: (value, item) => (
              <button
                className="btn btn-sm btn-outline-dark p-1 m-1"
                style={{
                  "--bs-btn-font-size": ".80rem",
                }}
                onClick={() => {
                  if (
                    !hasPermission(
                      null,
                      "can_update",
                      checkHrteachers(
                        hrTeachersData,
                        item.username,
                        schoolYear,
                      ),
                    )
                  ) {
                    handleToastCenterTop(
                      t("common.messages.hoomroomTeacherModifyOnly"),
                    );
                    return;
                  }
                  handleUpdateClick(item);
                }}
              >
                {value}
              </button>
            ),
          };
        }

        return {
          valueKey: header.headerKey,
          cellStyle:
            header.headerKey === "username" ? ellipsisCellStyle : wrapCellStyle,
          valueFormatter:
            header.headerKey === "username"
              ? (value) => (
                  <span style={ellipsisTextStyle} title={value || ""}>
                    {value}
                  </span>
                )
              : (value) => (Array.isArray(value) ? value.join(", ") : value),
        };
      }),
      rows: tableRows,
    };
  }, [
    tableRows,
    tableCustomFields,
    hasOtherName,
    t,
    hasPermission,
    hrTeachersData,
    schoolYear,
    handleToastCenterTop,
    handleUpdateClick,
  ]);

  const exportData = useMemo(() => {
    const headers = tableConfig.headers.map((header) => header.label);
    const rowsForExport = tableRows.map((row) =>
      tableConfig.headers.map((header) => {
        const value = row[header.headerKey];
        return Array.isArray(value) ? value.join(", ") : value || "";
      }),
    );

    return [headers, ...rowsForExport];
  }, [tableConfig.headers, tableRows]);

  if (isLoading) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access", checkHrteachers(hrTeachersData))) {
    return (
      <SimpleAlertMessageBlock
        message={
          t("common.messages.homeroomAccessOnly") ||
          "담임 또는 접근 권한이 있는 사람만 접근할 수 있습니다."
        }
      />
    );
  }

  return (
    <>
      <div className="ms-3 basicWidth">
        <div className="container-fluid">
          {isUpLoading && <LoadingModal />}
          <div className="row mb-4">
            {modalOpen && (
              <UpdateStuInfoModal
                setModalOpen={setModalOpen}
                studentInfo={studentInfo}
                setStudentInfo={setStudentInfo}
                setIsUpLoading={setIsUpLoading}
                studentData={studentData}
                customFieldSettings={customFieldSettings}
              />
            )}
            <div className="col-4">
              <div className="d-flex">
                <h3 className="fw-bold mt-3">
                  {t("teacher.studentInfo.studentBasicInfo")}
                </h3>
                <HelpHyperLink link={"https://youtu.be/3o6ID300gcw"} />
              </div>
            </div>
            <div className="col-8 d-flex gap-2 align-items-center">
              <select className="form-control" name="0" onChange={handleChange}>
                {settingData?.length > 0 && options}
                <option value="all">{t("common.labels.all")}</option>
              </select>
              {filteredSetting?.length > 0 &&
                Object.keys(idsForKey).map((item) => {
                  if (item === "0") return null;
                  return (
                    <select
                      key={item}
                      name={item}
                      className="form-control"
                      onChange={handleChange}
                    >
                      <option value="" data-name="">
                        {idsForKey[item][0]}
                      </option>
                      {filteredSetting
                        .filter(
                          (r) =>
                            r.parent_id ===
                            Number(filterOrgan[(Number(item) - 1).toString()]),
                        )
                        .sort((a, b) => {
                          const aValue = a["order"];
                          const bValue = b["order"];
                          if (
                            typeof aValue === "number" &&
                            typeof bValue === "number"
                          ) {
                            // 숫자인 경우
                            return aValue - bValue;
                          } else {
                            // 문자열인 경우
                            return aValue.localeCompare(bValue);
                          }
                        })
                        .map((option) => (
                          <option
                            key={option.id}
                            value={option.id}
                            data-name={option.organization_path}
                          >
                            {option.group_name}
                          </option>
                        ))}
                    </select>
                  );
                })}
            </div>
          </div>
          <div
            style={{
              width: "97vw",
              overflowX: "auto",
              maxWidth: "1536px",
            }}
          >
            {tableRows?.length > 0 && (
              <DynamicTable
                tableConfig={tableConfig}
                dataStructure="flat"
                tableStyle="cleanModern"
                pagination={true}
                pageSize={50}
                sortable={true}
                searchable={true}
                searchPlaceholder={t("teacher.studentInfo.searchStudentInfo") || "Search..."}
                searchFields={searchFields}
                // filterable={true}
                // filterableColumns={["gender", "enrollment_status"]}
                exportable={true}
                exportFileName={t("teacher.studentInfo.studentBasicInfo")}
                exportButtonLabel="Excel"
                customExportData={exportData}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
